<template>
	<loading-el	v-if="loading"></loading-el>
	<h2 class="popup-headline">{{ t('popup.quizzes.title') }}</h2>
	<div class="overview-area">
		<div class="overview-list">
			<div class="overview-item overview-item--create">
				<div
					v-if="isQuizCreating"
					class="overview-launch overview-launch--popup"
				>
					<div class="overview-launch__field">
						<input
							type="text"
							ref="quizNameField"
							v-model="quizName"
							:placeholder="t('popup.quizzes.placeholder.name')">
					</div>
					<div class="overview-launch__content">
						<div
							@click="quizCreate"
							class="overview-options__item overview-options__item--popup dark tooltip"
						>
							<i class="fas fa-rocket"></i>
							<div class="tooltip__item bottom">{{t('popup.quizzes.createQuiz')}}</div>
						</div>
						<div
							@click="quizCreating(false)"
							class="overview-options__item overview-options__item--popup trash dark tooltip"
						>
							<i class="fas fa-times"></i>
							<div class="tooltip__item bottom">{{t('clients.cancel')}}</div>
						</div>
					</div>
				</div>
				<figure
					v-else
					@click="quizCreating(true)"
					class="overview-item__figure overview-item__figure--popup"
				>
					<i class="fas fa-plus"></i>
				</figure>
				<div class="overview-item__describe">
					<div class="overview-item__info">
						<p>{{ t('popup.quizzes.createQuiz') }}</p>
					</div>
				</div>
			</div>
			
			<div
				v-for="(quiz, $quizIndex) in quizzes"
				:key="quiz.id"
				class="overview-item"
			>
				<figure
					@click.stop="isQuizChoosing ? quizChoose(quiz) : null"
					class="overview-item__figure overview-item__figure--popup"
				>
					<img v-if="quiz.preview_url" :src="hostURL + quiz.preview_url" alt="">
					<i v-else class="fas fa-puzzle-piece"></i>
					<div
						v-if="!isQuizChoosing"
						class="overview-item__options overview-options"
					>
						<div
							@click="quizEdit(quiz)"
							class="overview-options__item dark tooltip"
						>
							<i class="fas fa-pen"></i>
							<div class="tooltip__item bottom">Edit</div>
						</div>
						<div
							@click="deletingConfirm({id: quiz.id, index: $quizIndex})"
							class="overview-options__item dark tooltip"
						>
							<i class="fas fa-trash"></i>
							<div class="tooltip__item bottom"> {{ t('clients.deleteClient') }} </div>
						</div>
					</div>
				</figure>
				<div class="overview-item__describe">
					<div class="overview-item__info">
						<p>{{ quiz.title }}</p>
					</div>
				</div>
			</div>
			
		</div>
	</div>
	<popup-confirm
		:popup-status="popupActive"
		:content="`${t('popup.confirm.warning', {action: t('popup.confirm.actions.deleteMap')})} ${t('popup.confirm.cannotUndone')}<br>${t('popup.confirm.youSure')}`"
		@action-confirm="quizDelete"
		@action-cancel="deletingReject"
	></popup-confirm>
</template>

<script>
import {useI18n} from "vue-i18n";
import useLoading from '@/utils/Loading';
import LoadingEl from '@/components/ui/loading/LoadingEl.vue';
import useAlert from "@/utils/Alert";
import {ref, computed, nextTick, onMounted, onUnmounted} from "vue";
import {useStore} from "vuex";
import PopupConfirm from "@/components/popup/PopupConfirm";
import ApiService from "@/utils/ApiService";
import usePopup from "@/utils/Popup";
import {DASHBOARD_OPTIONS} from "@/models/DASHBOARD_OPTIONS";
import {ALERT_TYPES} from "@/models/ALERT_TYPES";
export default {
	components: {
		LoadingEl,
		PopupConfirm,
	},
	emits: ['chosenQuiz'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const hostURL = import.meta.env.VITE_ROOT_HOST;
		// alert
		const { alert, alertHide, alertShow, alertCreate } = useAlert();
		// loading
		const {loading, loadingShow, loadingHide} = useLoading(false);
		// store
		const store = useStore();
		// popup
		const { popupActive, popupOpen, popupClose } = usePopup();
		
		// STATES
		const isQuizCreating = ref(false);
		
		function quizCreating(status){
			isQuizCreating.value = status;
			nextTick(() => {
				if(status) quizNameField.value.focus();
			})
		}
		
		const isQuizChoosing = computed(() => store.getters["quizzes/isQuizChoosing"]);
		
		// DATA
		const quizzes = ref(null);
		const QUIZ_FORM = DASHBOARD_OPTIONS.ORGANIZE.GROUPS[0].QUIZ_FORM;
		
		// urls
		const mapsURL = computed(() => store.getters["maps/getMapsURL"]);
		const mapID = computed(() => store.getters["maps/getCurrentMapId"]);
		const quizzesURL = computed(() => store.getters["quizzes/getQuizzesURL"])
		
		// CRUD
		const quizName = ref('');
		const quizNameField = ref(null);
		
		function quizCreate(){
			if(!quizName.value.trim().length) {
				quizNameField.value.focus();
				return;
			}
			createQuizRequest();
		}
		function quizEdit(quiz){
			if(quiz.questions === undefined) quiz.questions = [];
			if(quiz.preview_url){
				quiz.preview_url = quiz.preview_url.includes(hostURL) ? quiz.preview_url : `${hostURL}${quiz.preview_url}`;
			}
			quizEditedSet(quiz);
			optionChoose(QUIZ_FORM);
		}
		
		const quizDeleting = ref(null);
		async function quizDelete(){
			popupClose();
			loadingShow();
			try{
				await ApiService.delete(`${mapsURL.value}${mapID.value}${quizzesURL.value}${quizDeleting.value.id}`);
				quizzes.value.splice(quizDeleting.value.index, 1);
				quizDeleting.value = null;
				loadingHide();
			} catch(error){
				alertCreate({type: ALERT_TYPES.ERROR, message: t('popup.editors.message.errorLoading')});
				alertShow();
				loadingHide();
			}
		}
		function quizChoose(quiz){
			const id = quiz.id;
			const title = quiz.title;
			const url = quiz.preview_url && !quiz.preview_url.includes(hostURL) ? `${hostURL}${quiz.preview_url}` : quiz.preview_url;
			emit('chosenQuiz', {id, url, title})
		}
		
		// CONFIRM
		function deletingConfirm({id, index}){
			quizDeleting.value = {id, index};
			popupOpen();
		}
		function deletingReject(){
			popupClose();
			quizDeleting.value = null;
		}
		
		// ACTIONS
		function quizEditedSet(quiz){
			store.dispatch("quizzes/quizEditedSet", quiz);
		}
		function optionChoose(option){
			store.dispatch('dashboardOptions/optionChoose', option);
		}
		
		// FETCH
		async function createQuizRequest(){
			loadingShow();
			try{
				const {data: quiz} = await ApiService.post(`${mapsURL.value}${mapID.value}${quizzesURL.value}`, {title: quizName.value});
				quizEdit(quiz);
			} catch(error){
				alertCreate({type: ALERT_TYPES.ERROR, message: t('popup.editors.message.errorLoading')});
				alertShow();
				loadingHide();
			}
		}
		
		async function getQuizzes(){
			loadingShow();
			try{
				({data: {results: quizzes.value}} = await ApiService.get(`${mapsURL.value}${mapID.value}${quizzesURL.value}`))
				loadingHide();
			} catch(error){
				alertCreate({type: ALERT_TYPES.ERROR, message: t('popup.editors.message.errorLoading')});
				alertShow();
				loadingHide();
			}
		}

    // TODO to avoid flashing of locked option provide status of editing quiz to store
    // SYNC SECTION LOCK
    const BLOCKED_TYPE = DASHBOARD_OPTIONS.ORGANIZE.GROUPS["0"].QUIZ.BLOCKED_TYPE;
    onMounted(() => {
      store.dispatch("simultaneousEditing/startEditingOption", BLOCKED_TYPE);
      console.log('QUIZ LIST MOUNTED');
    });
    onUnmounted(() => {
      store.dispatch("simultaneousEditing/stopEditingOption", BLOCKED_TYPE);
      console.log('QUIZ LIST UNMOUNTED');
    })
		
		// PROCESS
		getQuizzes();
		
		return {
			t,
			hostURL,
			loading,
			isQuizChoosing,
			isQuizCreating,
			quizCreating,
			quizName,
			quizNameField,
			quizCreate,
			quizEdit,
			quizDelete,
			quizChoose,
			quizzes,
			popupActive,
			deletingConfirm,
			deletingReject,
		}
	}
}
</script>

<style lang="scss">
@use "@/scss/sections/overview/overview-area";
@use "@/scss/sections/overview/overview-list";
@use "@/scss/sections/overview/overview-item";
@use "@/scss/sections/overview/overview-actions";
@use "@/scss/sections/overview/overview-options";
@use "@/scss/sections/overview/overview-launch";
</style>